import React, {useState, useEffect} from "react";
import {getAssignment} from "../../services/assignmentService";

const AssignmentTabView = ({session}) => {
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFields, setExpandedFields] = useState({});

  useEffect(() => {
    if (!session?.assignment_id) {
      setLoading(false);
      return;
    }

    async function fetchAssignment() {
      try {
        const data = await getAssignment(session.assignment_id);
        setAssignment(data.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching assignment:", err);
        setError("Failed to load assignment data");
        setLoading(false);
      }
    }

    fetchAssignment();
  }, [session?.assignment_id]);

  const toggleFieldExpansion = (key) => {
    setExpandedFields((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const shouldTruncate = (value) => {
    if (
      typeof value === "object" &&
      value !== null &&
      Object.keys(value).length !== 0
    )
      return true;
    return String(value || "").length > 50;
  };

  const formatValue = (key, value, isExpanded) => {
    if (value === null || value === undefined) return "-";

    let displayValue = value;

    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (typeof parsed === "object") {
          displayValue = parsed;
        }
      } catch {
        displayValue = value;
      }
    }

    if (typeof displayValue === "object") {
      return JSON.stringify(displayValue, null, isExpanded ? 2 : 0);
    }

    const stringValue = String(displayValue);
    if (stringValue.length > 50 && !isExpanded) {
      return `${stringValue.substring(0, 50)}...`;
    }

    return stringValue;
  };

  if (!session?.assignment_id) {
    return <p className="text-gray-400 p-4">No assignment data available</p>;
  }

  if (loading) {
    return <p className="text-gray-400 p-4">Loading assignment data...</p>;
  }

  if (error) {
    return <p className="text-red-400 p-4">{error}</p>;
  }

  if (!assignment) {
    return (
      <p className="text-gray-400 p-4">Assignment data could not be loaded</p>
    );
  }

  return (
    <div className="px-4 py-2 space-y-1">
      {Object
        .entries(assignment)
        .sort(([keyA, valA], [keyB, valB]) => keyA === 'id' ? -1 : keyB === "id" ? 1 : 0)
        .map(([key, value], index) => {
          const isExpanded = expandedFields[key] || false;
          const canExpand = shouldTruncate(value);

          return (
            <div key={key} className="group">
              <div className="flex justify-between items-start py-2">
                <div className="font-medium text-gray-300 capitalize w-1/3 text-left pt-1">
                  {key.replaceAll("_", " ")}
                </div>
                <div className="text-white w-2/3 text-right break-words">
                  <div
                    className={`${
                      isExpanded ? "whitespace-pre-wrap text-left" : "truncate"
                    }`}
                  >
                    {formatValue(key, value, isExpanded)}
                  </div>
                  {canExpand && (
                    <button
                      onClick={() => toggleFieldExpansion(key)}
                      className="text-xs text-blue-400 hover:text-blue-300 mt-1"
                    >
                      {isExpanded ? "Show less" : "Show more"}
                    </button>
                  )}
                </div>
              </div>
              {index < Object.entries(assignment).length - 1 && (
                <div className="border-b border-gray-700 my-1"></div>
              )}
            </div>
          );
        })}
    </div>
  );
};

export default AssignmentTabView;
